import { has } from 'lodash';
import {
  ISessionData,
  ISessionState,
  ISnapshotData,
  SessionStatus,
} from './types';
import { formatTime } from '../../services/timeService';
import { dateTimeFormat } from '../../services/timeService/types';
import Paths from './paths';
import { serverTimestamp } from 'firebase/database';

const isDeviceMain = (sessionData: ISessionData, deviceId: string): boolean => {
  if (!has(sessionData?.activeDevices, deviceId)) return false;
  const deviceNumber = sessionData.activeDevices[deviceId];
  return Object.values(sessionData.activeDevices).every(
    (num) => num >= deviceNumber
  );
};
export function createSessionData(sessionData: ISessionData): ISessionData {
  return {
    ...sessionData,
    isDeviceMain: (deviceId: string) => isDeviceMain(sessionData, deviceId),
  };
}

const getSessionStatus = (
  deviceId: string,
  sessionData?: ISessionData | null,
): SessionStatus => {
  if (!sessionData || !has(sessionData?.activeDevices, deviceId))
    return SessionStatus.INACTIVE;
  if (
    sessionData.activeDevices &&
    Object.keys(sessionData.activeDevices).length === 0
  ) {
    return SessionStatus.DISCONNECTED;
  }
  if (isDeviceMain(sessionData, deviceId)) return SessionStatus.ACTIVE_MAIN;
  return SessionStatus.ACTIVE_OBSERVING;
};

export function createSessionState(
  deviceId: string,
  sessionState = {
    sessionData: null,
  } as Partial<ISessionState>
): Partial<ISessionState> {
  const status = getSessionStatus(
    deviceId,
    sessionState?.sessionData,
  );
  return {
    ...sessionState,
    status,
  };
}

export const getSessionCompletionUpdates = (
  sessionKey: string,
  clinicianId: string,
  patientId: string,
  startTime: string,
  metadata: Record<string, string>
): {
  [key: string]: null | ISnapshotData;
} => {
  const timestamp = formatTime(
    startTime,
    dateTimeFormat.SERVER_DATE_TIME_WITH_S_FORMAT
  );
  return {
    [Paths.relativeActiveSession(sessionKey)]: null,
    [Paths.relativeCompletedSession(sessionKey, timestamp)]: {
      startTime: startTime,
      endTime: new Date().getTime(),
      metadata: metadata,
    },

    [Paths.relativePatientClinicians(patientId, clinicianId)]: null,
  };
};

export const getSessionReactivateUpdates = (
  sessionKey: string,
  recentCompletion: ISnapshotData,
  clinicianId: string,
  patientId: string,
  deviceId: string,
  deviceNumber: number
) => {
  return {
    [`${Paths.relativeActiveSession(sessionKey)}/startTime`]:
      recentCompletion.startTime || serverTimestamp(),
    [`${Paths.relativeActiveSession(sessionKey)}/metadata`]:
      recentCompletion.metadata || {},
    [`${Paths.relativeActiveSession(sessionKey)}/devices/${deviceId}`]:
      deviceNumber || 0,
    [Paths.relativePatientClinicians(patientId, clinicianId)]:
      serverTimestamp(),
  };
};

export const getDeviceCount = (sessionData: ISessionData) => 
  Object.keys(sessionData?.activeDevices ?? {}).length;